
import React, { useRef, useEffect, useState, useCallback } from 'react';
import mapboxgl, { Map } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { SiteAdminDetail } from 'src/api/Api';
import MapboxFeature from 'src/mapbox-helpers/MapboxFeature';
import { mapBoxConfig } from 'src/mapbox-helpers/config';
import { bbox } from '@turf/turf';

mapboxgl.accessToken = mapBoxConfig.accessToken;

interface AddLocationMapProps {
  site: SiteAdminDetail;
  onMapClick: (lngLat: { lng: number; lat: number }) => void;
  newLocation: { lat: number; lng: number } | null;
  onMarkerDrag: (lngLat: { lng: number; lat: number }) => void;
  showBoundary: boolean;
}

const AddLocationMap: React.FC<AddLocationMapProps> = ({ site, onMapClick, newLocation, onMarkerDrag, showBoundary }) => {
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<Map | null>(null);
  const newMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapStyle] = useState<string>(mapBoxConfig.mapStyle);
  const boundaryLayerId = `fill-boundary-${site.site_id}`;

  const fitMapToBoundaries = useCallback(() => {
    if (site.boundary && mapRef.current) {
      const tempFeature = new MapboxFeature();
      tempFeature.add(site.boundary, {});
      const geoJsonBoundary = tempFeature.toJSON();

      if (geoJsonBoundary && geoJsonBoundary.features.length > 0) {
        try {
          const calculatedBbox = bbox(geoJsonBoundary);
          mapRef.current.fitBounds(calculatedBbox as [number, number, number, number], {
            padding: 50,
            animate: false,
          });
        } catch (e) {
          console.error('Error calculating bounding box for single site:', e);
        }
      }
    }
  }, [site.boundary]);

  // Initialize map and add boundary layer
  useEffect(() => {
    if (mapRef.current) return; // initialize map only once
    if (!mapContainer.current) return;

    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: `mapbox://styles/mapbox/${mapStyle}`,
      center: [mapBoxConfig.defaulView.lng, mapBoxConfig.defaulView.lat],
      zoom: mapBoxConfig.defaulView.zoom,
    });

    map.addControl(new mapboxgl.NavigationControl(), 'bottom-right');
    map.addControl(new mapboxgl.FullscreenControl(), 'bottom-right');

    map.on('load', () => {
      setMapLoaded(true);
      if (site.boundary) {
        const sourceId = `boundary-${site.site_id}`;
        const polygon = new MapboxFeature();
        polygon.add(site.boundary, {});

        map.addSource(sourceId, {
          type: 'geojson',
          data: polygon.toJSON(),
        });

        map.addLayer({
          id: boundaryLayerId,
          type: 'fill',
          source: sourceId,
          paint: {
            'fill-color': mapBoxConfig.polygon.fillColor,
            'fill-opacity': 0.2,
          },
          filter: ['==', '$type', 'Polygon'],
        });
        fitMapToBoundaries();
      }
    });

    map.on('click', (e) => {
      onMapClick(e.lngLat);
    });

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [mapStyle, onMapClick, site, boundaryLayerId, fitMapToBoundaries]);

  // Handle boundary visibility
  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;
    const layer = mapRef.current.getLayer(boundaryLayerId);
    if (layer) {
      mapRef.current.setPaintProperty(boundaryLayerId, 'fill-opacity', showBoundary ? 0.2 : 0);
    }
  }, [showBoundary, mapLoaded, boundaryLayerId]);

  // Handle new location marker
  useEffect(() => {
    if (!mapRef.current) return;

    // Remove existing marker first
    const existingMarker = newMarkerRef.current;
    if (existingMarker) {
      existingMarker.remove();
      newMarkerRef.current = null;
    }

    // Add new marker if location is provided
    if (newLocation) {
      const marker = new mapboxgl.Marker({
        draggable: true,
      })
        .setLngLat([newLocation.lng, newLocation.lat])
        .addTo(mapRef.current);

      const onDragEnd = () => {
        const lngLat = marker.getLngLat();
        onMarkerDrag({ lng: lngLat.lng, lat: lngLat.lat });
      };

      marker.on('dragend', onDragEnd);

      newMarkerRef.current = marker;
    }
  }, [newLocation, onMarkerDrag]);

  return <div ref={mapContainer} style={{ position: 'absolute', top: 0, bottom: 0, width: '100%' }} />;
};

export default AddLocationMap;
